if (!customElements.get('cart-tier-progress')) {
  class CartTierProgress extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      try {
        this.tiers = JSON.parse(this.dataset.tiers || '[]');
      } catch (_error) {
        this.tiers = [];
      }
      // Settings are four free-form number fields; nothing stops a merchant
      // entering them out of order, and the max/next lookups assume ascending.
      this.tiers = this.tiers
        .filter((tier) => tier && Number.isFinite(tier.qty) && tier.qty > 0)
        .sort((a, b) => a.qty - b.qty);
      if (!this.tiers.length) return;

      this.count = Number.parseInt(this.dataset.count || '0', 10) || 0;
      this.freeShipFrom = Number.parseInt(this.dataset.freeShipFrom || '0', 10) || 0;
      this.productCounts = this.dataset.productCounts === 'true';

      this.onCartUpdate = (event) => {
        const eventCount = event.detail?.cart?.item_count;
        if (Number.isFinite(eventCount)) {
          this.scheduleRender(eventCount);
        } else {
          void this.refetchCount();
        }
      };
      this.onWindowRefresh = () => void this.refetchCount();

      document.addEventListener('cart:update', this.onCartUpdate);
      window.addEventListener('pageshow', this.onWindowRefresh);
      window.addEventListener('focus', this.onWindowRefresh);

      // The header bubble is rewritten by every cart path, including the
      // vendor bundle's own add-to-cart, so it doubles as a change signal.
      // #cart-icon-bubble is the stable wrapper that survives those swaps.
      const bubbleHost = document.getElementById('cart-icon-bubble');
      if (bubbleHost) {
        this.bubbleObserver = new MutationObserver(() => {
          const bubbleCount = this.readBubbleCount();
          if (bubbleCount == null) {
            void this.refetchCount();
          } else {
            this.scheduleRender(bubbleCount);
          }
        });
        this.bubbleObserver.observe(bubbleHost, { childList: true, subtree: true, characterData: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this.onCartUpdate);
      window.removeEventListener('pageshow', this.onWindowRefresh);
      window.removeEventListener('focus', this.onWindowRefresh);
      this.bubbleObserver?.disconnect();
      this.bubbleObserver = null;
      clearTimeout(this.renderTimer);
      delete this.dataset.ready;
    }

    readBubbleCount() {
      const host = document.getElementById('cart-icon-bubble');
      if (!host) return null;
      const bubble = host.querySelector('.cart-count-bubble');
      if (!bubble) return 0;
      const text = bubble.querySelector('span[aria-hidden="true"]')?.textContent?.trim();
      // The count span is omitted at 100+ items; force a real fetch then.
      if (!text || !/^\d+$/.test(text)) return null;
      return Number.parseInt(text, 10);
    }

    async refetchCount() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const cart = await response.json();
        this.scheduleRender(cart.item_count || 0);
      } catch (_error) {
        // Keep the last known state rather than flashing a wrong tier.
      }
    }

    scheduleRender(count) {
      clearTimeout(this.renderTimer);
      this.renderTimer = setTimeout(() => {
        if (count === this.count) return;
        this.count = count;
        this.render();
      }, 200);
    }

    getTierBoundaries(count) {
      const first = this.tiers[0];
      const max = this.tiers[this.tiers.length - 1];
      let unlocked = null;
      let next = null;
      this.tiers.forEach((tier) => {
        if (tier.qty <= count) unlocked = tier;
        if (tier.qty > count && !next) next = tier;
      });
      return { first, max, unlocked, next };
    }

    rewardText(tier, mobile) {
      let reward = `${tier.pct}% OFF`;
      if (this.freeShipFrom > 0 && tier.qty >= this.freeShipFrom) {
        reward += mobile ? ' + Free ship' : ' + Free shipping';
      }
      return reward;
    }

    // Mirrors the Liquid in snippets/cart-tier-progress.liquid — any copy
    // change must be made in both places. Returns HTML (the figures are bold).
    getMessage(count) {
      const { first, max, unlocked, next } = this.getTierBoundaries(count);

      if (count >= max.qty) {
        return {
          state: 'max',
          desktop: `<strong>Maximum savings unlocked: ${this.rewardText(max)}</strong> 🏆`,
          mobile: `<strong>Max savings: ${this.rewardText(max, true)}</strong> 🏆`,
        };
      }

      if (!unlocked) {
        const needed = first.qty - count;
        if (count === 0) {
          return {
            state: 'first',
            desktop: `Add <strong>${first.qty} items</strong> to unlock <strong>${this.rewardText(first)}</strong> 🛒`,
            mobile: `Add <strong>${first.qty} items</strong> for <strong>${this.rewardText(first, true)}</strong> 🛒`,
          };
        }
        const word = `${needed} more item${needed === 1 ? '' : 's'}`;
        return {
          state: 'first',
          desktop: `Only <strong>${word}</strong> to unlock <strong>${this.rewardText(first)}</strong> 🎉`,
          mobile: `Only <strong>${word}</strong> for <strong>${this.rewardText(first, true)}</strong> 🎉`,
        };
      }

      const needed = next.qty - count;
      const word = `${needed} more item${needed === 1 ? '' : 's'}`;
      const html = `Only <strong>${word}</strong> to unlock <strong>${next.pct}% OFF</strong> 🎉`;
      return { state: 'next', desktop: html, mobile: html };
    }

    // Milestones sit at equal spacing (the centres of the four tiles); the
    // fill is interpolated by item count between them. Same rule as Liquid.
    getFillPercent(count) {
      const total = this.tiers.length;
      if (!total || count <= 0) return 0;
      if (count >= this.tiers[total - 1].qty) return 100;
      let prevQty = 0;
      let prevPos = 0;
      for (let index = 0; index < total; index += 1) {
        const tier = this.tiers[index];
        const pos = ((index + 0.5) / total) * 100;
        if (count <= tier.qty) {
          return prevPos + ((count - prevQty) / (tier.qty - prevQty)) * (pos - prevPos);
        }
        prevQty = tier.qty;
        prevPos = pos;
      }
      return 100;
    }

    render() {
      const message = this.getMessage(this.count);
      const box = this.querySelector('[data-tier-message]');
      if (box) {
        box.classList.remove('is-max', 'is-first', 'is-next');
        box.classList.add(`is-${message.state}`);
      }
      const desktop = this.querySelector('[data-tier-message-desktop]');
      const mobile = this.querySelector('[data-tier-message-mobile]');
      if (desktop) desktop.innerHTML = message.desktop;
      if (mobile) mobile.innerHTML = message.mobile;

      const fill = this.querySelector('[data-tier-fill]');
      if (fill) fill.style.width = `${this.getFillPercent(this.count).toFixed(2)}%`;

      this.querySelectorAll('[data-tier-dot]').forEach((dot) => {
        const qty = Number.parseInt(dot.dataset.tierDot || '0', 10);
        dot.classList.toggle('is-done', this.count >= qty);
      });

      const { unlocked } = this.getTierBoundaries(this.count);
      this.querySelectorAll('.cart-tier-progress__milestone').forEach((milestone) => {
        const qty = Number.parseInt(milestone.dataset.tierQty || '0', 10);
        let state = 'upcoming';
        if (unlocked && unlocked.qty === qty) state = 'applied';
        else if (this.count >= qty) state = 'completed';

        milestone.classList.remove('is-completed', 'is-applied', 'is-upcoming', 'is-current');
        milestone.classList.add(`is-${state}`);
        if (state === 'applied') {
          milestone.setAttribute('aria-current', 'step');
        } else {
          milestone.removeAttribute('aria-current');
        }

        const baseLabel = (milestone.getAttribute('aria-label') || '').replace(/ — (unlocked|applied now)$/, '');
        const suffix = state === 'applied' ? ' — applied now' : state === 'completed' ? ' — unlocked' : '';
        milestone.setAttribute('aria-label', baseLabel + suffix);
      });
    }
  }

  customElements.define('cart-tier-progress', CartTierProgress);
}
